import React from "react";

// ============================= Types =============================
export type HistoricoItem = {
  id: number;
  produto_id: number;
  movimentacao_id?: number;
  tipo?:
    | "entrada"
    | "saida"
    | "perda"
    | "venda"
    | "consumo"
    | "retorno"
    | "ajuste"
    | "transferir"
    | string
    | null;
  custo_unitario: string | number | null;
  quantidade: number | null;
  data_movimentacao: string | null;
  responsavel_id: number | null;
  responsavel_nome?: string | null;
  moeda?: string | null; // <<< NOVO
};

export type HistoricoWidgetProps = {
  items: HistoricoItem[];
  title?: string;
  loading?: boolean;
  error?: string | null;
  formatCurrency?: (
    v: string | number | null | undefined,
    moeda?: string | null
  ) => string;
  formatDateTime?: (iso: string | null | undefined) => string;
  onRowClick?: (item: HistoricoItem) => void;
};

// ======================== Default formatters ======================
const defaultFormatCurrency = (
  v: string | number | null | undefined,
  moeda?: string | null
) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "—";
  const cur =
    moeda === "USD" || moeda === "EUR" || moeda === "BRL" ? moeda : "BRL";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: cur,
  }).format(n);
};

const defaultFormatDateTime = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const data = d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const hora = d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${data} - ${hora}`;
};

// ============================= Layout =============================
const COLUMNS = ["Responsável", "Q.", "Tipo", "Custo", "Data"] as const;
const gridTemplate = "2fr 1fr 1fr 1fr 1.5fr";

const C_PASTEL_YELLOW = "#FFF9DB"; // standby-baixa / transferir / standby-venda (não contabiliza)
const C_PASTEL_RED = "#FDEAEA"; // standby / perda / venda / consumo (saídas)
const C_PASTEL_GREEN = "#EAF7EA"; // entrada / retorno (entradas)
const C_PASTEL_BLUE = "#E7F1FF"; // ajuste (baseline)

const ACCENT_YELLOW = "#E5C100";
const ACCENT_RED = "#D66";
const ACCENT_GREEN = "#7BBF7B";
const ACCENT_BLUE = "#4A90E2";

const styles: { [k: string]: React.CSSProperties } = {
  wrapper: {
    border: "1px solid #000",
    width: "100%",
    marginTop: 30,
    fontFamily: "Inter, sans-serif",
    boxSizing: "border-box",
  },
  top: {
    height: 35,
    backgroundColor: "#124280",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 14,
    borderBottom: "1px solid #000",
  },
  legendWrap: {
    padding: "10px 12px",
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center",
    borderBottom: "1px solid #000",
    background: "#fafafa",
    fontSize: 12,
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#333",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
  swatch: {
    width: 14,
    height: 14,
    borderRadius: 3,
    border: "1px solid rgba(0,0,0,0.15)",
    boxSizing: "border-box",
  },
  legendDivider: {
    flexBasis: "100%",
    height: 0,
  },
  formula: {
    fontSize: 12,
    color: "#333",
    opacity: 0.9,
  },
  headerRow: {
    height: 35,
    backgroundColor: "#124280CF",
    display: "grid",
    gridTemplateColumns: gridTemplate,
    alignItems: "center",
    color: "#fff",
    fontWeight: 600,
    fontSize: 13,
    boxSizing: "border-box",
    borderBottom: "1px solid #000",
  },
  headerCell: {
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    boxSizing: "border-box",
    borderRight: "1px solid #000",
  },
  body: {
    backgroundColor: "#fff",
    display: "grid",
    gridTemplateColumns: gridTemplate,
    alignItems: "stretch",
    fontSize: 13,
    boxSizing: "border-box",
  },
  bodyCell: {
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: "#333",
    boxSizing: "border-box",
    borderRight: "1px solid #000",
  },
  error: {
    padding: 12,
    color: "#c0362c",
    fontSize: 13,
    borderTop: "1px solid #000",
  },
};

function capitalizeTipo(tipo?: string | null) {
  if (!tipo) return "—";
  return tipo.charAt(0).toUpperCase() + tipo.slice(1);
}

// <<< ATUALIZAÇÃO: inclui "standby-venda" como amarelo (não contabiliza)
function getRowBackground(tipo?: string | null): string | undefined {
  const t = String(tipo || "").toLowerCase();
  if (
    t === "standby-baixa" ||
    t === "standby-baixo" || // se vier com typo
    t === "transferir" ||
    t === "standby-venda" // <<< novo
  ) {
    return C_PASTEL_YELLOW; // não contabiliza
  }
  if (t === "standby" || t === "perda" || t === "venda" || t === "consumo") {
    return C_PASTEL_RED; // debita -x
  }
  if (t === "entrada" || t === "retorno") {
    return C_PASTEL_GREEN; // credita +x
  }
  if (t === "ajuste") {
    return C_PASTEL_BLUE; // baseline
  }
  return undefined;
}

// <<< ATUALIZAÇÃO: inclui "standby-venda" como amarelo (não contabiliza)
function getLeftBorderColor(tipo?: string | null): string | undefined {
  const t = String(tipo || "").toLowerCase();
  if (
    t === "standby-baixa" ||
    t === "standby-baixo" ||
    t === "transferir" ||
    t === "standby-venda" // <<< novo
  ) {
    return ACCENT_YELLOW; // não contabiliza
  }
  if (t === "standby" || t === "perda" || t === "venda" || t === "consumo") {
    return ACCENT_RED; // debita -x
  }
  if (t === "entrada" || t === "retorno") {
    return ACCENT_GREEN; // credita +x
  }
  if (t === "ajuste") {
    return ACCENT_BLUE; // baseline
  }
  return undefined;
}

// =========================== Component ============================
const HistoricoWidget: React.FC<HistoricoWidgetProps> = ({
  items,
  title = "Histórico de Movimentações do Produto",
  loading = false,
  error = null,
  formatCurrency = defaultFormatCurrency,
  formatDateTime = defaultFormatDateTime,
  onRowClick,
}) => {
  const showEmpty = !loading && (!items || items.length === 0);

  return (
    <div style={styles.wrapper}>
      <div style={styles.top}>{title}</div>

      {/* Legenda / Explicação */}
      <div style={styles.legendWrap}>
        {/* Amarelo: confirmações / transferências (não contabilizam) */}
        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: C_PASTEL_YELLOW }} />
          <span>
            <strong>Standby-baixa</strong>, <strong>Standby-venda</strong> ou{" "}
            <strong>Transferir</strong>: não contabiliza
            (confirmação/transferência)
          </span>
        </div>

        {/* Vermelho: saídas que debitam */}
        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: C_PASTEL_RED }} />
          <span>
            <strong>Standby</strong>, <strong>Perda</strong>,{" "}
            <strong>Venda</strong> ou <strong>Consumo</strong>: contabiliza{" "}
            <strong>−x</strong> (saída do estoque)
          </span>
        </div>

        {/* Verde: entradas que somam */}
        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: C_PASTEL_GREEN }} />
          <span>
            <strong>Entrada</strong> / <strong>Retorno</strong>: contabiliza{" "}
            <strong>+x</strong>
          </span>
        </div>

        {/* Azul: baseline */}
        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: C_PASTEL_BLUE }} />
          <span>
            <strong>Ajuste</strong>: redefine o saldo base (<em>x = 0 + y</em>)
          </span>
        </div>

        <div style={styles.legendDivider} />

        <div style={styles.formula}>
          <strong>Fórmula (simplificada):</strong>{" "}
          <code>
            Saldo teórico = Σ Entradas (+x) + Σ Retornos (+y) − Σ Standby (x) −
            Σ Perdas (x) − Σ Vendas (x) − Σ Consumos (x)
          </code>{" "}
          <span style={{ opacity: 0.9 }}>
            {" | "}Movimentos amarelos (ex.: <em>Standby-baixa</em> e{" "}
            <em>Standby-venda</em>) servem como confirmação/transferência e{" "}
            <u>não entram</u> no cômputo do saldo por este widget.
          </span>
        </div>
      </div>

      {/* Cabeçalho */}
      <div style={styles.headerRow}>
        {COLUMNS.map((label, idx) => (
          <div
            key={label}
            style={{
              ...styles.headerCell,
              borderRight:
                idx === COLUMNS.length - 1 ? "none" : "1px solid #000",
            }}
          >
            {label}
          </div>
        ))}
      </div>

      {/* Corpo */}
      <div style={styles.body}>
        {showEmpty &&
          Array.from({ length: COLUMNS.length }).map((_, idx) => (
            <div
              key={`empty-${idx}`}
              style={{
                ...styles.bodyCell,
                borderRight:
                  idx === COLUMNS.length - 1 ? "none" : "1px solid #000",
              }}
            >
              —
            </div>
          ))}

        {items?.map((c, rowIdx) => {
          const bg = getRowBackground(c.tipo);
          const leftColor = getLeftBorderColor(c.tipo);
          const cells = [
            c.responsavel_nome || (c.responsavel_id ?? "—"),
            c.quantidade ?? "—",
            capitalizeTipo(c.tipo),
            formatCurrency(c.custo_unitario, c.moeda ?? null),
            formatDateTime(c.data_movimentacao),
          ];

          return cells.map((val, colIdx) => (
            <div
              key={`${c.id}-${colIdx}`}
              onClick={onRowClick ? () => onRowClick(c) : undefined}
              style={{
                ...styles.bodyCell,
                background: bg,
                borderRight:
                  colIdx === COLUMNS.length - 1 ? "none" : "1px solid #000",
                borderTop: rowIdx === 0 ? "none" : "1px solid #000",
                borderLeft:
                  colIdx === 0 && leftColor ? `4px solid ${leftColor}` : "none",
                cursor: onRowClick ? "pointer" : "default",
              }}
            >
              {val}
            </div>
          ));
        })}
      </div>

      {error && (
        <div style={styles.error}>Erro ao carregar histórico: {error}</div>
      )}
    </div>
  );
};

export default HistoricoWidget;
